/**
 * Runtime Configuration Utility
 * 
 * This utility allows accessing environment variables at runtime from the window object.
 * This is useful for Docker/container deployments where env vars are set at runtime.
 * 
 * Usage:
 *   import { getRuntimeConfig } from '@/libs/runtime-config'
 *   const apiUrl = getRuntimeConfig('NEXT_PUBLIC_API_URL', 'http://localhost:5000')
 */

// Type definition for runtime config
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      [key: string]: string | undefined
    }
  }
  var __RUNTIME_CONFIG__: { [key: string]: string | undefined } | undefined
}

/**
 * Get a runtime environment variable from globalThis.__RUNTIME_CONFIG__
 * Falls back to process.env if window config is not available
 * 
 * @param key - The environment variable key (e.g., 'NEXT_PUBLIC_API_URL')
 * @param defaultValue - Default value if the key is not found
 * @returns The environment variable value or defaultValue
 */
export function getRuntimeConfig(key: string): string | undefined
export function getRuntimeConfig(key: string, defaultValue: string): string
export function getRuntimeConfig(key: string, defaultValue?: string): string | undefined {
  // In browser/client-side
  if (globalThis.window) {
    // First, try to get from globalThis.__RUNTIME_CONFIG__ (runtime, set by script tag)
    // This is the preferred method for Docker/container deployments
    const runtimeVal = globalThis.__RUNTIME_CONFIG__?.[key]
    if (runtimeVal) return runtimeVal

    // Fallback to process.env (build-time, only works for NEXT_PUBLIC_* vars)
    // Note: In Docker, this might not work if env vars are set after build
    if (process.env[key]) {
      return process.env[key]
    }
  }

  // Server-side: use process.env
  // This reads from the actual environment at request time (works in Docker)
  if (globalThis.window === undefined) {
    // Try the exact key first
    if (process.env[key]) {
      return process.env[key]
    }

    // For NEXT_PUBLIC_* vars, also try without the prefix (some Docker setups)
    if (key.startsWith('NEXT_PUBLIC_')) {
      const keyWithoutPrefix = key.replace('NEXT_PUBLIC_', '')
      if (process.env[keyWithoutPrefix]) {
        return process.env[keyWithoutPrefix]
      }
    }
  }

  return defaultValue
}

/**
 * Get all runtime config values
 */
export function getAllRuntimeConfig(): Record<string, string | undefined> {
  const config = globalThis.__RUNTIME_CONFIG__
  if (globalThis.window && config) return config
  return {}
}

/**
 * Check if runtime config is available
 */
export function hasRuntimeConfig(): boolean {
  return !!globalThis.window && !!globalThis.__RUNTIME_CONFIG__
}
